/**
 * NextRunCell — what the NEXT rotation does to this row, on ONE line so the
 * column stays scannable: verb, then the replacement that takes its place.
 * The rule's reason is the least urgent part, so it moves to the hover title
 * rather than costing every row a second and third line.
 *
 * Nothing planned renders NOTHING — a column full of em dashes is noise, and
 * the summary above already states how many rows are untouched.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  ArrowRight,
  CircleSlash,
  Clock,
  Hourglass,
  Trash2,
} from 'lucide-react';
import type { RotationElementDiff } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import type { RotationSwap } from '../../../rotation/rotation-swaps';
import type { RowTone } from '../testing-grid';

export interface NextRunCellProps {
  /** The planned swap taking this element OUT next run. */
  swap?: RotationSwap;
  /** The planned diff HOLDING this element (pool floor / capacity / no stats). */
  held?: RotationElementDiff;
}

/** How the row is painted: the verdict's own colour, or nothing at all. */
export function rowTone({ swap, held }: NextRunCellProps): RowTone {
  if (swap?.out) return swap.out.action === 'eliminate' ? 'eliminate' : 'requeue';
  return held ? 'held' : 'none';
}

const NextRunCell: FC<NextRunCellProps> = ({ swap, held }) => {
  const out = swap?.out;
  if (out) {
    const requeue = out.action === 'requeue';
    // Capacity evictions park in WAITING — say so, not "back to queue".
    const toWaiting = requeue && Boolean(out.toWaiting);
    const Icon = toWaiting ? Hourglass : requeue ? Clock : Trash2;
    return (
      <span
        className="inline-flex min-w-0 items-center gap-1.5 text-[11px]"
        title={out.reason ? `Rule: ${out.reason}` : undefined}
      >
        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 font-semibold',
            requeue
              ? 'bg-primary/15 text-primary'
              : 'bg-destructive/15 text-destructive',
          )}
        >
          <Icon className="h-3 w-3 shrink-0" aria-hidden />
          {toWaiting ? 'To waiting' : requeue ? 'Back to queue' : 'Eliminate'}
        </span>
        {swap?.in ? (
          <>
            <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate font-medium text-success">{swap.in.itemName}</span>
          </>
        ) : null}
      </span>
    );
  }
  if (held) {
    // Three calm-to-urgent flavors: no-stats (muted "Ignored"), pair-hold
    // (blue "Waiting for replacement" — the rule swaps, the queue is empty),
    // genuine block (amber "Held").
    const Icon = held.noStats
      ? CircleSlash
      : held.pairHold
        ? ArrowLeftRight
        : AlertTriangle;
    return (
      <span
        className={cn(
          'inline-flex min-w-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold',
          held.noStats
            ? 'bg-muted text-muted-foreground'
            : held.pairHold
              ? 'bg-primary/15 text-primary'
              : 'bg-warning/15 text-warning',
        )}
        title={
          held.noStats
            ? "Ignored — no stats in the rule's window yet"
            : (held.reason ?? undefined)
        }
      >
        <Icon className="h-3 w-3 shrink-0" aria-hidden />
        {held.noStats
          ? 'Ignored'
          : held.pairHold
            ? 'Waiting for replacement'
            : 'Held'}
      </span>
    );
  }
  return null;
};

export default NextRunCell;
